import { ref, watch } from 'vue';

const COOKIE_CONSENT_KEY = 'uni_hospitalar_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'uni_hospitalar_cookie_preferences';
const CONSENT_EXPIRATION_DAYS = 365;

const cookiePreferences = ref({
  essential: true, // Sempre true
  analytics: false,
  marketing: false
});

const showCookieBanner = ref(true);

export const useCookieManagement = () => {
  // Inicializar preferências do localStorage
  const initializeCookies = () => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (savedConsent && savedPreferences) {
      showCookieBanner.value = false;
      const prefs = JSON.parse(savedPreferences);
      cookiePreferences.value = prefs;
      loadCookieScripts(prefs);
    } else {
      showCookieBanner.value = true;
    }
  };

  // Salvar preferências de cookies
  const saveCookiePreferences = (preferences) => {
    cookiePreferences.value = preferences;

    // Salvar no localStorage
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      timestamp: new Date().getTime(),
      expiresAt: new Date(Date.now() + CONSENT_EXPIRATION_DAYS * 24 * 60 * 60 * 1000).toISOString()
    }));

    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));

    // Carregar scripts baseado nas preferências
    loadCookieScripts(preferences);

    // Esconder banner
    showCookieBanner.value = false;
  };

  // Aceitar todos os cookies
  const acceptAllCookies = () => {
    const preferences = {
      essential: true,
      analytics: true,
      marketing: true
    };
    saveCookiePreferences(preferences);
  };

  // Rejeitar cookies opcionais
  const rejectOptionalCookies = () => {
    const preferences = {
      essential: true,
      analytics: false,
      marketing: false
    };
    saveCookiePreferences(preferences);
  };

  // Carregar scripts de cookies baseado nas preferências
  const loadCookieScripts = (preferences) => {
    // Google Analytics
    if (preferences.analytics) {
      loadGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }

    // Google Ads / Facebook Pixel
    if (preferences.marketing) {
      loadMarketingScripts();
    } else {
      disableMarketingScripts();
    }

    // Essential cookies (sempre carregado)
    if (preferences.essential) {
      loadEssentialScripts();
    }
  };

  // Carregar Google Analytics
  const loadGoogleAnalytics = () => {
    if (window.GA_LOADED) return;

    const GA_ID = 'G-457YKPT3DR';

    // Criar script do Google Analytics
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag('js', new Date());
    gtag('config', GA_ID, {
      'anonymize_ip': true,
      'allow_google_signals': true,
      'allow_ad_personalization_signals': true
    });

    window.GA_LOADED = true;
  };

  // Desabilitar Google Analytics
  const disableGoogleAnalytics = () => {
    if (window.GA_LOADED) {
      // Remover as configurações do Google Analytics
      window['ga-disable-G-457YKPT3DR'] = true;
    }
  };

  // Carregar scripts de marketing
  const loadMarketingScripts = () => {
    if (window.MARKETING_LOADED) return;

    // Facebook Pixel
    const facebookPixelId = 'SEU_PIXEL_ID_AQUI'; // Substituir
    if (facebookPixelId !== 'SEU_PIXEL_ID_AQUI') {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${facebookPixelId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);

      // noscript fallback
      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${facebookPixelId}&ev=PageView&noscript=1" />`;
      document.body.appendChild(noscript);
    }

    window.MARKETING_LOADED = true;
  };

  // Desabilitar scripts de marketing
  const disableMarketingScripts = () => {
    // Remover scripts de marketing do window
    if (window.fbq) {
      delete window.fbq;
    }
  };

  // Carregar scripts essenciais (sempre executados)
  const loadEssentialScripts = () => {
    if (window.ESSENTIAL_LOADED) return;

    // Scripts essenciais para funcionamento do site
    // Como hotjar, sentry, etc podem ir aqui
    
    window.ESSENTIAL_LOADED = true;
  };

  // Resetar consentimento de cookies
  const resetCookieConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    showCookieBanner.value = true;
    cookiePreferences.value = {
      essential: true,
      analytics: false,
      marketing: false
    };
  };

  // Verificar se há consentimento salvo
  const hasConsentExpired = () => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) return true;

    try {
      const consent = JSON.parse(savedConsent);
      const expiresAt = new Date(consent.expiresAt);
      return expiresAt < new Date();
    } catch (e) {
      return true;
    }
  };

  // Validar e atualizar consentimento expirado
  const validateConsent = () => {
    if (hasConsentExpired()) {
      resetCookieConsent();
    } else {
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        const prefs = JSON.parse(savedPreferences);
        loadCookieScripts(prefs);
      }
    }
  };

  // Watch para sincronizar com localStorage
  watch(
    () => cookiePreferences.value,
    (newPrefs) => {
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPrefs));
    },
    { deep: true }
  );

  return {
    cookiePreferences,
    showCookieBanner,
    initializeCookies,
    saveCookiePreferences,
    acceptAllCookies,
    rejectOptionalCookies,
    resetCookieConsent,
    validateConsent,
    loadCookieScripts
  };
};

